import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { toast } from "react-toastify";
import TablePagination from "./TablePagination";
import { Modal, Button } from "react-bootstrap";
import { getRefunds, updateRefundStatus } from "../Apis/RefundAPIs";
import { getExposList } from "../Apis/ExpoAPIs";
import { getManagers } from "../Apis/ManagerAPIs";

const RefundListLayer = () => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedExpo, setSelectedExpo] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "Pending" | "Paid"

  // Dropdown options
  const [expoOptions, setExpoOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);

  // Modal State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeRefund, setActiveRefund] = useState(null);
  const [refundNotes, setRefundNotes] = useState("");

  // Statistics
  const [stats, setStats] = useState({ total: 0, pending: 0, paid: 0 });

  const getRejectReason = (statusUpdateDetails) => {
    if (!statusUpdateDetails || !Array.isArray(statusUpdateDetails)) return "-";
    const rejectedDetail = [...statusUpdateDetails].reverse().find(d => d.status === "Rejected");
    return rejectedDetail?.remark || "-";
  };

  // Load expo & manager options on mount
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [expoRes, managerRes] = await Promise.all([
          getExposList({ limit: 1000 }),
          getManagers({ limit: 1000 }),
        ]);

        if (expoRes?.status) {
          const expos = expoRes.response?.data || expoRes.response || [];
          setExpoOptions(
            expos.map((e) => ({
              value: e._id || e.id,
              label: e.expoName || e.name,
            }))
          );
        }

        if (managerRes?.status) {
          const managers = managerRes.response?.data || managerRes.response || [];
          setManagerOptions(
            managers.map((m) => ({
              value: m._id || m.id,
              label: m.name,
            }))
          );
        }
      } catch (err) {
        console.error("Failed to load filter dropdowns:", err);
      }
    };
    loadDropdowns();
  }, []);

  // Fetch refunds from backend
  const fetchRefunds = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        expoId: selectedExpo?.value || undefined,
        managerId: selectedManager?.value || undefined,
        refundStatus: statusFilter === "all" ? undefined : statusFilter,
      };

      const result = await getRefunds(params);
      if (result?.status) {
        const data = result.response;
        setRefunds(data.data || []);
        setStats(data.counts || { total: 0, pending: 0, paid: 0 });
        setTotalItems(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching refunds:", error);
      toast.error("Failed to fetch refund list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds();
  }, [currentPage, searchTerm, selectedExpo, selectedManager, statusFilter, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setSelectedExpo(null);
    setSelectedManager(null);
    setStatusFilter("all");
    setSearchTerm("");
    setCurrentPage(0);
  };

  const handleOpenConfirmModal = (refund) => {
    setActiveRefund(refund);
    setRefundNotes("");
    setShowConfirmModal(true);
  };

  const handleConfirmRefund = async () => {
    if (!refundNotes.trim()) {
      toast.warning("Please enter refund remarks/details before confirming");
      return;
    }

    try {
      const result = await updateRefundStatus(activeRefund._id, {
        refundStatus: "Yes",
        remarks: refundNotes,
      });

      if (result?.status) {
        toast.success("Refund status updated to Paid successfully!");
        setShowConfirmModal(false);
        fetchRefunds();
      } else {
        const msg = result?.response?.message || "Failed to update refund status";
        toast.error(msg);
      }
    } catch (error) {
      console.error("Error confirming refund:", error);
      toast.error("Failed to confirm refund");
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
      {/* Header */}
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{
          background:
            "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)",
        }}
      >
        <h6 className="text-white mb-0 fw-bold">Refund List</h6>
        <div className="d-flex align-items-center gap-2">
          <span
            className="badge bg-dark px-12 py-6 text-white fw-semibold"
            style={{ fontSize: "13px" }}
          >
            Total: {stats.total}
          </span>
          <span
            className="badge bg-warning px-12 py-6 text-white fw-semibold"
            style={{ fontSize: "13px" }}
          >
            Pending: {stats.pending}
          </span>
          <span
            className="badge bg-success px-12 py-6 text-white fw-semibold"
            style={{ fontSize: "13px" }}
          >
            Paid: {stats.paid}
          </span>
        </div>
      </div>

      {/* Filter Bar */}
      <div
        className="px-24 py-16 border-bottom"
        style={{ backgroundColor: "#fafafa" }}
      >
        <div className="d-flex align-items-end flex-wrap gap-3">
          {/* Expo Name Filter */}
          <div style={{ minWidth: "220px", flex: "1 1 220px", maxWidth: "280px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Expo Name
            </label>
            <Select
              options={expoOptions}
              value={selectedExpo}
              onChange={(val) => {
                setSelectedExpo(val);
                setCurrentPage(0);
              }}
              isClearable
              placeholder="All Expos"
              styles={selectStyles()}
            />
          </div>

          {/* Manager Filter */}
          <div style={{ minWidth: "220px", flex: "1 1 220px", maxWidth: "280px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Manager Name
            </label>
            <Select
              options={managerOptions}
              value={selectedManager}
              onChange={(val) => {
                setSelectedManager(val);
                setCurrentPage(0);
              }}
              isClearable
              placeholder="All Managers"
              styles={selectStyles()}
            />
          </div>

          {/* Search bar */}
          <div style={{ minWidth: "200px", flex: "1 1 200px", maxWidth: "260px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Search
            </label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control radius-8 ps-40"
                style={{ height: "44px" }}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <Icon
                icon="ion:search-outline"
                className="position-absolute top-50 translate-middle-y text-secondary-light"
                style={{ fontSize: "18px", left: "12px" }}
              />
            </div>
          </div>

          {/* Refund Status Toggle */}
          <div>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1 d-block"
              style={{ letterSpacing: "0.5px" }}
            >
              Refund Status
            </label>
            <div
              className="d-flex align-items-center gap-1 p-1 radius-8 border bg-white"
              style={{ height: "44px" }}
            >
              <button
                type="button"
                className="btn btn-sm fw-bold radius-6 px-12"
                style={{
                  height: "34px",
                  backgroundColor: statusFilter === "all" ? "#bb0007" : "transparent",
                  color: statusFilter === "all" ? "#fff" : "#6b7280",
                  border: "none",
                  fontSize: "12px",
                  transition: "all 0.2s",
                }}
                onClick={() => {
                  setStatusFilter("all");
                  setCurrentPage(0);
                }}
              >
                All
              </button>
              <button
                type="button"
                className="btn btn-sm fw-bold radius-6 px-12"
                style={{
                  height: "34px",
                  backgroundColor: statusFilter === "Pending" ? "#d97706" : "transparent",
                  color: statusFilter === "Pending" ? "#fff" : "#6b7280",
                  border: "none",
                  fontSize: "12px",
                  transition: "all 0.2s",
                }}
                onClick={() => {
                  setStatusFilter("Pending");
                  setCurrentPage(0);
                }}
              >
                Pending
              </button>
              <button
                type="button"
                className="btn btn-sm fw-bold radius-6 px-12"
                style={{
                  height: "34px",
                  backgroundColor: statusFilter === "Paid" ? "#16a34a" : "transparent",
                  color: statusFilter === "Paid" ? "#fff" : "#6b7280",
                  border: "none",
                  fontSize: "12px",
                  transition: "all 0.2s",
                }}
                onClick={() => {
                  setStatusFilter("Paid");
                  setCurrentPage(0);
                }}
              >
                Paid
              </button>
            </div>
          </div>

          {/* Reset Filters */}
          <div className="d-flex align-items-end">
            <button
              className="btn btn-outline-danger h-44-px radius-8 fw-bold d-flex align-items-center justify-content-center gap-2 px-20"
              onClick={handleClearFilters}
              title="Clear Filters"
            >
              <Icon icon="solar:restart-bold" className="text-xl" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Card Body & Table */}
      <div className="card-body p-24">
        <div className="table-responsive border radius-12 shadow-sm mb-4">
          <table className="table bordered-table sm-table mb-0 text-nowrap">
            <thead>
              <tr
                style={{
                  background: "linear-gradient(135deg, #bb0007, #ed1c24)",
                }}
              >
                <th
                  scope="col"
                  style={{ color: "white", fontWeight: "bold" }}
                  className="px-24 w-70-px text-center"
                >
                  S.No
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Expo Name
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Manager Name
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Exhibitor Name
                </th>
                <th
                  scope="col"
                  style={{ color: "white", fontWeight: "bold" }}
                  className="text-center w-100-px"
                >
                  Stall No
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Amount
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Reject Reason
                </th>
                <th
                  scope="col"
                  style={{ color: "white", fontWeight: "bold" }}
                  className="text-center w-120-px"
                >
                  Refund Status
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Refund Remarks
                </th>
                <th
                  scope="col"
                  style={{ color: "white", fontWeight: "bold" }}
                  className="text-center w-150-px"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-48">
                    <div className="spinner-border text-danger" role="status"></div>
                    <div className="mt-12 text-secondary-light fw-medium">
                      Loading refund list...
                    </div>
                  </td>
                </tr>
              ) : refunds.length > 0 ? (
                refunds.map((item, index) => (
                  <tr key={item._id} className="hover-bg-light transition-all">
                    <td className="px-24 text-center fw-medium text-secondary-light">
                      {currentPage * itemsPerPage + index + 1}
                    </td>
                    <td className="fw-semibold text-secondary-light">{item.expoName}</td>
                    <td className="text-secondary-light">
                      {item.managerNames && item.managerNames.length > 0
                        ? item.managerNames.join(", ")
                        : "-"}
                    </td>
                    <td className="text-secondary-light">{item.name}</td>
                    <td className="text-center fw-bold text-secondary-light">{item.stallNo}</td>
                    <td className="text-secondary-light fw-bold">₹{item.amount?.toLocaleString()}</td>
                    <td
                      style={{
                        maxWidth: "250px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                      className="text-secondary-light"
                    >
                      {getRejectReason(item.statusUpdateDetails)}
                    </td>
                    <td className="text-center">
                      <span
                        className={`badge ${
                          item.refundStatus === "Paid"
                            ? "bg-success-focus text-success-main"
                            : "bg-warning-focus text-warning-main"
                        } px-12 py-6 radius-4 fw-bold uppercase`}
                        style={{ fontSize: "10px", minWidth: "80px" }}
                      >
                        {item.refundStatus === "Paid" ? "PAID" : "PENDING"}
                      </span>
                    </td>
                    <td
                      style={{
                        maxWidth: "280px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                      className="text-secondary-light"
                    >
                      {item.remarks || <span className="text-neutral-400 italic">No notes added</span>}
                    </td>
                    <td className="text-center">
                      {item.refundStatus !== "Paid" ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger radius-6 px-16 py-8 d-inline-flex align-items-center gap-2 fw-bold"
                          style={{
                            backgroundColor: "#ed1c24",
                            border: "none",
                            fontSize: "12px",
                            boxShadow: "0 2px 4px rgba(237, 28, 36, 0.2)",
                          }}
                          onClick={() => handleOpenConfirmModal(item)}
                        >
                          <Icon icon="solar:check-read-linear" className="text-md" />
                          Confirm
                        </button>
                      ) : (
                        <span className="text-success fw-bold d-flex align-items-center justify-content-center gap-1">
                          <Icon icon="solar:check-circle-bold" className="text-lg" />
                          Completed
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-48 text-secondary-light fw-medium">
                    No refund transactions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer actions and pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-24">
          <div></div>
          <TablePagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            onPageChange={handlePageChange}
            rowsPerPage={itemsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            totalRecords={totalItems}
          />
        </div>
      </div>

      {/* Confirm Refund Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        contentClassName="radius-16 border-0 shadow-lg"
        dialogClassName="w-500-px mx-auto"
      >
        <Modal.Body className="p-32">
          <div className="d-flex justify-content-center mb-24">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="solar:info-square-bold" className="text-danger-600 text-xxl" />
            </div>
          </div>

          <h5 className="fw-bold mb-8 text-center">Confirm Refund Transaction</h5>
          <p className="text-secondary-light mb-24 text-center">
            Are you sure you want to change the refund status for{" "}
            <strong>{activeRefund?.name}</strong> (Stall {activeRefund?.stallNo}) to{" "}
            <strong>Paid</strong>? This action is permanent.
          </p>

          <div className="mb-24">
            <label className="form-label fw-bold text-secondary-light mb-8">
              Refund Remarks / Reference Details <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control radius-8"
              rows="3"
              placeholder="e.g. Bank transfer transaction ID, or cash hand-over details..."
              value={refundNotes}
              onChange={(e) => setRefundNotes(e.target.value)}
              style={{ resize: "none" }}
            />
          </div>

          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-24 py-10 radius-8 fw-semibold"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="px-24 py-10 radius-8 border-0 fw-bold"
              onClick={handleConfirmRefund}
              style={{ backgroundColor: "#ed1c24", color: "white" }}
            >
              Confirm & Save
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RefundListLayer;
